import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Compass } from 'lucide-react';

const techLabels = [
  { label: 'AI Solutions', angle: 0, radius: 160 },
  { label: 'Cloud & DevOps', angle: 60, radius: 160 },
  { label: 'Custom Dev', angle: 120, radius: 160 },
  { label: 'QA / Testing', angle: 180, radius: 160 },
  { label: 'Digital Strategy', angle: 240, radius: 160 },
  { label: 'Data & Analytics', angle: 300, radius: 160 },
];

const OrbitalGraphic = () => (
  <div className="relative w-[340px] h-[340px] md:w-[420px] md:h-[420px] flex-shrink-0">
    {/* Orbit rings */}
    <svg className="absolute inset-0 w-full h-full" viewBox="0 0 420 420" fill="none">
      <circle cx="210" cy="210" r="155" stroke="rgba(0,212,255,0.12)" strokeWidth="1" strokeDasharray="4 6" />
      <circle cx="210" cy="210" r="100" stroke="rgba(0,212,255,0.18)" strokeWidth="1" strokeDasharray="2 4" />
      <circle cx="210" cy="210" r="50" stroke="rgba(0,212,255,0.25)" strokeWidth="1" />
      {/* Center diamond */}
      <polygon
        points="210,175 240,210 210,245 180,210"
        fill="rgba(0,212,255,0.06)"
        stroke="rgba(0,212,255,0.4)"
        strokeWidth="1.5"
      />
      {techLabels.map((item, i) => {
        const rad = (item.angle * Math.PI) / 180;
        const x = 210 + item.radius * Math.cos(rad);
        const y = 210 + item.radius * Math.sin(rad);
        return (
          <g key={i}>
            <line
              x1="210" y1="210" x2={x} y2={y}
              stroke="rgba(0,212,255,0.08)" strokeWidth="1"
            />
            <circle cx={x} cy={y} r="3" fill="#00d4ff" opacity="0.7" />
          </g>
        );
      })}
      {/* Animated dot on outer orbit */}
      <circle r="5" fill="#00d4ff" opacity="0.9">
        <animateMotion dur="8s" repeatCount="indefinite">
          <mpath href="#orbitPath" />
        </animateMotion>
      </circle>
      <path id="orbitPath" d="M 210,55 A 155,155 0 1,1 209.99,55" fill="none" />
    </svg>

    {/* Center logo */}
    <div className="absolute inset-0 flex items-center justify-center">
      <div className="relative">
        <img src="/logo.png" alt="IronGate" className="w-16 h-16 object-contain opacity-90" />
        <div className="absolute inset-0 bg-cyan-400/20 blur-xl rounded-full" />
      </div>
    </div>

    {/* Floating labels */}
    {techLabels.map((item, i) => {
      const rad = (item.angle * Math.PI) / 180;
      const pct = 50 + ((item.radius + 28) / 420) * 100 * Math.cos(rad);
      const pctY = 50 + ((item.radius + 28) / 420) * 100 * Math.sin(rad);
      return (
        <div
          key={i}
          className="absolute text-[11px] font-medium px-2.5 py-1 rounded-full whitespace-nowrap"
          style={{
            left: `${pct}%`,
            top: `${pctY}%`,
            transform: 'translate(-50%, -50%)',
            background: 'rgba(8,13,26,0.85)',
            border: '1px solid rgba(0,212,255,0.25)',
            color: '#94a3b8',
            backdropFilter: 'blur(8px)',
          }}
        >
          {item.label}
        </div>
      );
    })}
  </div>
);

const Hero = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 100);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden pt-20">
      {/* Background */}
      <div className="absolute inset-0 circuit-pattern opacity-30" />
      <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] rounded-full blur-3xl animate-pulse-slow"
        style={{ background: 'radial-gradient(circle, rgba(0,212,255,0.06) 0%, transparent 70%)' }} />
      <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-3xl animate-pulse-slow"
        style={{ background: 'radial-gradient(circle, rgba(0,102,255,0.07) 0%, transparent 70%)', animationDelay: '3s' }} />

      {/* Horizontal separator lines */}
      <div className="absolute top-1/2 left-0 w-full h-px bg-gradient-to-r from-transparent via-cyan-500/15 to-transparent" />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 w-full">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12">

          {/* Left: Text content */}
          <div className="flex-1 max-w-2xl">
            {/* Badge */}
            <div className="inline-flex items-center space-x-2 rounded-full px-4 py-1.5 mb-8"
              style={{ background: 'rgba(0,212,255,0.08)', border: '1px solid rgba(0,212,255,0.25)' }}>
              <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              <span className="text-xs font-medium text-cyan-400 tracking-wide uppercase">Dublin, Ireland · Available for Projects</span>
            </div>

            {/* Heading */}
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold leading-[1.08] mb-6 tracking-tight">
              <span className="text-white">Digital</span>
              <br />
              <span className="gradient-text">Transformation.</span>
              <br />
              <span className="text-white">AI that</span>{' '}
              <span className="gradient-text">delivers.</span>
            </h1>

            <p className="text-lg text-gray-400 mb-10 leading-relaxed max-w-xl">
              We help companies modernise legacy systems, implement AI, and build custom software — 
              backed by 15 years of enterprise experience across Fintech, Payments, and Healthcare.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-start gap-4 mb-10">
              <a
                href="#contact"
                onClick={e => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                className="group relative inline-flex items-center space-x-2 px-7 py-3.5 text-white font-semibold rounded-lg overflow-hidden transition-all duration-300"
                style={{ background: 'linear-gradient(135deg, #00b8e6 0%, #0066ff 100%)' }}
              >
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'linear-gradient(135deg, #00d4ff 0%, #0088ff 100%)' }} />
                <span className="relative z-10">Start a Project</span>
                <ArrowRight className="relative z-10 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>

              <Link
                to="/navigator"
                className="inline-flex items-center space-x-2 px-7 py-3.5 font-semibold rounded-lg transition-all duration-200"
                style={{
                  background: 'rgba(0,212,255,0.07)',
                  border: '1px solid rgba(0,212,255,0.3)',
                  color: '#00d4ff',
                }}
              >
                <Compass className="w-4 h-4" />
                <span>QE Navigator →</span>
              </Link>
            </div>

            {/* Stats row */}
            <div className="flex items-center gap-8 pt-4 border-t border-white/5">
              {[
                { value: '15+', label: 'Years experience' },
                { value: '3', label: 'Industries' },
                { value: '100%', label: 'Results focus' },
              ].map((stat, i) => (
                <div key={i}>
                  <div className="text-2xl font-bold gradient-text">{stat.value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Orbital graphic */}
          <div className="hidden lg:flex justify-center items-center">
            <OrbitalGraphic />
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className={`absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce transition-opacity duration-500 ${scrolled ? 'opacity-0' : 'opacity-100'}`}>
        <div className="w-5 h-9 border border-cyan-500/40 rounded-full flex items-start justify-center p-1.5">
          <div className="w-1 h-2.5 bg-cyan-500 rounded-full animate-pulse" />
        </div>
      </div>
    </section>
  );
};

export default Hero;
